import {
  Box,
  HStack,
  JsxStyleProps,
  RadioGroup,
  useToken
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import { useEffect, useMemo, useRef, useState } from "react"
import Pathicon from "../pathicon/Pathicon"

export type RadioPillOption = {
  value: string
  label?: string | React.ReactNode
  render?: (option: RadioPillOption) => React.ReactNode
  icon?: IconName
  indicator?: boolean
}

export type RadioPillsProps = {
  options: RadioPillOption[]
  activeIndex: number
  onChange: (index: number, option: RadioPillOption) => void
  iconMode?: boolean
  brand?: boolean
  size?: "sm" | "md" | "lg"
  spacing?: JsxStyleProps["gap"]
  className?: string
}

type MeasuredRadioProps = {
  index: number
  onMeasure: (index: number, rect: DOMRect) => void
  isActive: boolean
  iconMode: boolean
  option: RadioPillOption
  size: "sm" | "md" | "lg"
  // radioOption: ReturnType<UseRadioGroupReturn["getRadioProps"]>
}

function MeasuredRadio({
  index,
  onMeasure,
  isActive,
  iconMode,
  option,
  size
}: // radioOption
MeasuredRadioProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      onMeasure(index, rect)
    }
    const raf = requestAnimationFrame(measure)

    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        const target = (entry && (entry.target as HTMLElement)) || el
        const rect = target.getBoundingClientRect()
        onMeasure(index, rect)
      })
      observer.observe(el)
    } else {
      window.addEventListener("resize", measure)
    }

    return () => {
      cancelAnimationFrame(raf)
      if (observer) observer.disconnect()
      else window.removeEventListener("resize", measure)
    }
  }, [index, onMeasure])

  const sizeToPy: Record<"sm" | "md" | "lg", number> = { sm: 1, md: 1.5, lg: 2 }

  return (
    <RadioGroup.Item
      ref={ref}
      value={index.toString()}
      display="inline-block"
      className={`RadioPills-radio RadioPills-radio-${option.value} ${
        isActive ? "radio-pill-active" : ""
      }`}
    >
      <RadioGroup.ItemHiddenInput />
      <Box
        className={`RadioPills-radio-visual`}
        as="span"
        px={4}
        py={sizeToPy[size]}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        fontWeight={600}
        transition="background-color 150ms ease"
        color={"white"}
        _hover={{ backgroundColor: "transparent" }}
        _active={{ backgroundColor: "transparent" }}
        _focus={{ backgroundColor: "transparent" }}
        _focusVisible={{ boxShadow: "none", outline: "none" }}
        cursor={"pointer"}
        css={{
          "& svg": {
            margin: 0
          }
        }}
      >
        {option.render ? (
          option.render(option)
        ) : (
          <>
            {!!option.icon && (
              <Pathicon icon={option.icon} mr={!iconMode ? 2 : 0} />
            )}
            {!iconMode && option.label !== undefined && option.label}
            {option.indicator && (
              <Box
                as="span"
                ml={2}
                boxSize={2}
                borderRadius="full"
                backgroundColor={isActive ? "white" : "brand.600"}
                display="inline-block"
              />
            )}
          </>
        )}
      </Box>
    </RadioGroup.Item>
  )
}

const TRANSITION_INTERVAL_MS = 250

const sizeToPadding: Record<"sm" | "md" | "lg", number> = {
  sm: 1.5,
  md: 2,
  lg: 2.5
}

const RadioPills = ({
  options,
  activeIndex,
  onChange,
  iconMode = false,
  brand = false,
  size = "md",
  spacing = 0,
  className
}: RadioPillsProps) => {
  const [sizes, setSizes] = useState<DOMRect[]>([])

  // Sum widths of all elements to the left of the active one
  const leftOffsetPx = useMemo(() => {
    return sizes
      .filter((_, i) => i < activeIndex)
      .reduce((sum, rect) => sum + (rect?.width || 0), 0)
  }, [sizes, activeIndex])

  const activeRect = sizes[activeIndex]

  const [brand500, grayBg] = useToken("colors", [
    "brand.600",
    "rgba(153, 153, 153, 0.5)"
  ])

  const containerPaddingPx = sizeToPadding[size]
  const containerPadding = `${containerPaddingPx}px`
  const allMeasured =
    sizes.length === options.length &&
    sizes.every((r) => r && !isNaN(r.width) && !isNaN(r.height))

  const isReady =
    allMeasured &&
    !!activeRect &&
    !isNaN(activeRect.width) &&
    !isNaN(activeRect.height)

  const transformTransition = `${TRANSITION_INTERVAL_MS}ms ease-in-out`
  // Front-load width progression so it reaches its target earlier in the motion
  const widthTransition = `${TRANSITION_INTERVAL_MS}ms cubic-bezier(0.3, 0, 0.2, 1)`

  return (
    <RadioGroup.Root
      value={String(activeIndex)}
      onValueChange={(e) => {
        const idx = Number(e.value)
        if (!isNaN(idx)) {
          onChange(idx, options[idx])
        }
      }}
      className={`RadioPills ${className || ""}`}
      position="relative"
      overflow="hidden"
      // borderRadius="full"
      padding={containerPadding}
      whiteSpace="nowrap"
    >
      {/* Frosted background */}
      <Box
        position="absolute"
        className="RadioPills-background"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={grayBg}
        backdropFilter="blur(20px)"
        borderRadius="full"
      />

      {/* Moving highlight */}
      {isReady && (
        <RadioGroup.Context>
          {(radioGroup) => (
            <Box
              className={`RadioPills-highlight${
                radioGroup.value
                  ? ` RadioPills-highlight-${radioGroup.value}`
                  : ""
              }`}
              position="absolute"
              top={containerPadding}
              left={containerPadding}
              height={`calc(100% - (${containerPadding} * 2))`}
              width={`${activeRect.width}px`}
              transform={`translateX(${leftOffsetPx}px)`}
              transition={`transform ${transformTransition}, width ${widthTransition}`}
              borderRadius="full"
              backdropFilter="blur(20px)"
              backgroundColor={brand ? brand500 : "rgba(42,46,51,0.9)"}
              zIndex={1}
              style={{
                willChange: "transform, width",
                transformOrigin: "left center"
              }}
              pointerEvents="none"
            />
          )}
        </RadioGroup.Context>
      )}

      {/* Radios */}
      <HStack
        gap={spacing}
        position="relative"
        zIndex={2}
        height={`calc(100% - (${containerPadding} * 2))`}
      >
        {options.map((option, index) => (
          <MeasuredRadio
            key={option.value || index}
            index={index}
            onMeasure={(i, rect) => {
              setSizes((prev) => {
                const prevRect = prev[i]
                if (
                  prevRect &&
                  Math.round(prevRect.width) === Math.round(rect.width) &&
                  Math.round(prevRect.height) === Math.round(rect.height)
                ) {
                  return prev
                }
                const next = [...prev]
                next[i] = rect
                return next
              })
            }}
            isActive={index === activeIndex}
            iconMode={iconMode}
            option={option}
            size={size}
          />
        ))}
      </HStack>
    </RadioGroup.Root>
  )
}

export default RadioPills
